import React, { useState, useEffect } from "react";
import {
  Box, Typography, InputBase, Button,
  Avatar, IconButton, Tooltip, Dialog, DialogContent,
  Select, MenuItem, FormControl, InputLabel, Collapse,
  Checkbox, Divider,
} from "@mui/material";
import GroupAddIcon          from "@mui/icons-material/GroupAdd";
import SearchIcon            from "@mui/icons-material/Search";
import DeleteIcon            from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon  from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloseIcon             from "@mui/icons-material/Close";
import { useTheme }          from "@mui/material/styles";
import { useSelector }       from "react-redux";
import { ReduxRootStateType } from "../redux/store";
import { useGetUsersByCompany } from "../api/reactQueryHook/usePeople";
import { User_Info_Type }    from "../api/axiosNetworkRequest/people";
import {
  useGetRolesByCompany,
  useCreateRole,
  useDeleteRole,
  useGetUsersByRole,
  useAddUserToRole,
  useRemoveUserFromRole,
} from "../api/reactQueryHook/useRoles";
import { Role_Type }   from "../api/axiosNetworkRequest/roles";
import LoadingOverlay  from "./LoadingOverlay";

const MagicOverlay: React.FC<{ show: boolean }> = ({ show }) => <LoadingOverlay isLoading={show} />;

// ─── Types ────────────────────────────────────────────────────────────────────
interface PeopleViewProps {
  selectedCompanyId: number;
}

type PeopleTab = "PEOPLE" | "ROLES";

// ─── Role Membership Checker ─────────────────────────────────────────────────
// Invisible component — fires useGetUsersByRole per role and reports membership
const RoleMembershipChecker: React.FC<{
  roleId: number;
  userId: number;
  onResult: (roleId: number, isMember: boolean) => void;
}> = ({ roleId, userId, onResult }) => {
  const { data } = useGetUsersByRole(roleId);
  useEffect(() => {
    if (data) onResult(roleId, data.data.some(m => m.user_id === userId));
  }, [data]);
  return null;
};

// ─── Add To Role Dialog ───────────────────────────────────────────────────────
const AddToRoleDialog: React.FC<{
  open: boolean;
  user: User_Info_Type | null;
  roles: Role_Type[];
  assignedBy: number;
  onClose: () => void;
}> = ({ open, user, roles, assignedBy, onClose }) => {
  const [selectedRoleId,  setSelectedRoleId]  = useState<number | "">("");
  const [disabledRoleIds, setDisabledRoleIds] = useState<Set<number>>(new Set());
  const addMutation = useAddUserToRole();

  useEffect(() => {
    if (open) setDisabledRoleIds(new Set());
  }, [open, user?.user_id]);

  const handleAdd = () => {
    if (!selectedRoleId || !user) return;
    addMutation.mutate(
      { role_id: Number(selectedRoleId), payload: { user_ids: [user.user_id], assigned_by: assignedBy } },
      { onSuccess: () => { setSelectedRoleId(""); onClose(); } }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "10px",
          overflow: "hidden",
        },
      }}
    >
      <MagicOverlay show={addMutation.isPending} />
      {open && user && roles.map(role => (
        <RoleMembershipChecker
          key={role.role_id}
          roleId={role.role_id}
          userId={user.user_id}
          onResult={(id, isMember) => {
            if (isMember) setDisabledRoleIds(prev => new Set([...prev, id]));
          }}
        />
      ))}
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 3, pt: 2.5, pb: 2,
          borderBottom: "1px solid", borderColor: "divider",
        }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary" }}>
              Add to Role
            </Typography>
            {user && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.3, fontSize: "0.72rem" }}>
                {user.first_name} {user.last_name}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose}
            sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Dropdown */}
        <Box sx={{ px: 3, py: 3 }}>
          {roles.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 2 }}>
              No roles found. Create a role first.
            </Typography>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: "text.secondary" }}>Select a Role</InputLabel>
              <Select
                value={selectedRoleId}
                label="Select a Role"
                onChange={e => setSelectedRoleId(Number(e.target.value))}
                sx={{
                  color: "text.primary",
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
                  ".MuiSvgIcon-root": { color: "text.secondary" },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                    },
                  },
                }}
              >
                {roles.map(role => (
                  <MenuItem key={role.role_id} value={role.role_id} disabled={disabledRoleIds.has(role.role_id)}>
                    <Typography variant="body2" sx={{ color: disabledRoleIds.has(role.role_id) ? "text.disabled" : "text.primary" }}>
                      {role.role_name}{disabledRoleIds.has(role.role_id) ? " (already assigned)" : ""}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Footer */}
        {roles.length > 0 && (
          <Box sx={{ px: 3, pb: 2.5, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ fontWeight: 600, borderRadius: "8px", fontSize: "0.8rem" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!selectedRoleId || addMutation.isPending}
              onClick={handleAdd}
              sx={{ fontWeight: 700, borderRadius: "8px", fontSize: "0.8rem" }}
            >
              {addMutation.isPending ? "Adding..." : "Add to Role"}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Role Row ─────────────────────────────────────────────────────────────────
const RoleRow: React.FC<{
  role: Role_Type;
  companyUsers: User_Info_Type[];
  assignedBy: number;
  onDelete: (role_id: number, role_name: string) => void;
}> = ({ role, companyUsers, assignedBy, onDelete }) => {
  const theme = useTheme();
  const [expanded,        setExpanded]        = useState(false);
  const [addPeopleOpen,   setAddPeopleOpen]   = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());

  const { data: usersData, isLoading } = useGetUsersByRole(role.role_id);
  const addMutation        = useAddUserToRole();
  const removeUserMutation = useRemoveUserFromRole();
  const members    = usersData?.data ?? [];
  const memberIds  = new Set(members.map(m => m.user_id));
  const available  = companyUsers.filter(u => !memberIds.has(u.user_id));

  const toggleUser = (id: number) =>
    setSelectedUserIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleAddPeople = () => {
    if (selectedUserIds.size === 0) return;
    addMutation.mutate(
      { role_id: role.role_id, payload: { user_ids: [...selectedUserIds], assigned_by: assignedBy } },
      { onSuccess: () => { setSelectedUserIds(new Set()); setAddPeopleOpen(false); } }
    );
  };

  return (
    <Box sx={{
      mb: 1.5, borderRadius: "10px",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      overflow: "hidden",
    }}>
      <MagicOverlay show={removeUserMutation.isPending} />
      {/* Role header row */}
      <Box
        onClick={() => setExpanded(p => !p)}
        sx={{
          display: "flex", alignItems: "center", gap: 1.5,
          px: 3, py: 2, cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
          transition: "background-color 0.15s",
        }}
      >
        <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
        <Typography sx={{ flex: 1, color: "text.primary", fontWeight: 600, fontSize: "0.95rem" }}>
          {role.role_name}
        </Typography>
        <Tooltip title="Add People">
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); setAddPeopleOpen(true); }}
            sx={{ color: "primary.main", "&:hover": { color: "primary.light", bgcolor: "rgba(25,118,210,0.08)" } }}
          >
            <GroupAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete Role">
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); onDelete(role.role_id, role.role_name); }}
            sx={{ color: "error.main", "&:hover": { color: "#c62828", bgcolor: "rgba(239,83,80,0.08)" } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Members list */}
      <Collapse in={expanded}>
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, px: 3, py: 1.5 }}>
          {isLoading ? (
            <Typography variant="body2" sx={{ color: "text.secondary", py: 1 }}>
              Loading members...
            </Typography>
          ) : members.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", py: 1 }}>
              No members in this role.
            </Typography>
          ) : (
            members.map(member => (
              <Box
                key={member.user_role_id}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.5, py: 1.2,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Avatar sx={{
                  width: 32, height: 32, fontSize: "0.75rem", fontWeight: 700,
                  bgcolor: "background.default", color: "text.primary",
                }}>
                  {member.first_name[0]}{member.last_name[0]}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: "text.primary", fontSize: "0.88rem", fontWeight: 500 }}>
                    {member.first_name} {member.last_name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {member.email_id}
                  </Typography>
                </Box>
                <Tooltip title="Remove from Role">
                  <IconButton
                    size="small"
                    onClick={() => removeUserMutation.mutate({ role_id: role.role_id, user_role_id: member.user_role_id })}
                    sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))
          )}
        </Box>
      </Collapse>

      {/* Add People Dialog */}
      <Dialog
        open={addPeopleOpen}
        onClose={() => { setAddPeopleOpen(false); setSelectedUserIds(new Set()); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            border: "1px solid", borderColor: "divider",
            borderRadius: "10px", overflow: "hidden",
          },
        }}
      >
        <MagicOverlay show={addMutation.isPending} />
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", maxHeight: "70vh" }}>
          {/* Header */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid", borderColor: "divider",
          }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary" }}>
                Add People to Role
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.3, fontSize: "0.72rem" }}>
                {role.role_name}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => { setAddPeopleOpen(false); setSelectedUserIds(new Set()); }}
              sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* User list */}
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {available.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 4 }}>
                All users are already in this role.
              </Typography>
            ) : (
              available.map(user => {
                const checked = selectedUserIds.has(user.user_id);
                return (
                  <Box key={user.user_id}>
                    <Box
                      onClick={() => toggleUser(user.user_id)}
                      sx={{
                        display: "flex", alignItems: "center", gap: 2,
                        px: 3, py: 1.8, cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                        transition: "background-color 0.12s",
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        onChange={() => {}}
                        sx={{
                          p: 0, color: "text.disabled",
                          "&.Mui-checked": { color: "primary.main" },
                          "& .MuiSvgIcon-root": { fontSize: "1.4rem" },
                        }}
                      />
                      <Box>
                        <Typography sx={{ color: "text.primary", fontSize: "0.9rem", fontWeight: 500 }}>
                          {user.first_name} {user.last_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {user.email_id}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ borderColor: "divider" }} />
                  </Box>
                );
              })
            )}
          </Box>

          {/* Footer */}
          {available.length > 0 && (
            <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
              <Button
                variant="outlined"
                onClick={() => { setAddPeopleOpen(false); setSelectedUserIds(new Set()); }}
                sx={{ fontWeight: 600, borderRadius: "8px", fontSize: "0.8rem" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={selectedUserIds.size === 0 || addMutation.isPending}
                onClick={handleAddPeople}
                sx={{ fontWeight: 700, borderRadius: "8px", fontSize: "0.8rem" }}
              >
                {addMutation.isPending ? "Adding..." : `Add ${selectedUserIds.size > 0 ? `(${selectedUserIds.size})` : ""}`}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// ─── User Row ─────────────────────────────────────────────────────────────────
const UserRow: React.FC<{
  user: User_Info_Type;
  onAddToRole: (user: User_Info_Type) => void;
}> = ({ user, onAddToRole }) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex", alignItems: "center", gap: 2,
        px: 3, py: 2, mb: 1.5, borderRadius: "10px",
        backgroundColor: hovered ? "action.hover" : theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        transition: "background-color 0.15s ease",
        minHeight: "72px",
      }}
    >
      <Avatar sx={{
        width: 42, height: 42,
        backgroundColor: theme.palette.background.default,
        fontSize: "0.85rem", fontWeight: 700,
        color: theme.palette.text.primary,
      }}>
        {user.first_name[0]}{user.last_name[0]}
      </Avatar>

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <Typography sx={{
          color: theme.palette.text.primary, fontSize: "1rem", fontWeight: 400,
          lineHeight: 1.5, letterSpacing: "0.15008px",
        }}>
          {user.first_name} {user.last_name}
        </Typography>
        <Typography variant="caption" sx={{
          color: theme.palette.text.secondary,
          display: "block",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {user.email_id}
        </Typography>
      </Box>

      <Tooltip title="Add to Role">
        <IconButton
          size="small"
          onClick={e => { e.stopPropagation(); onAddToRole(user); }}
          sx={{
            color: "primary.main",
            "&:hover": { color: "primary.light", backgroundColor: "rgba(25,118,210,0.08)" },
          }}
        >
          <GroupAddIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ─── People View ──────────────────────────────────────────────────────────────
const PeopleView: React.FC<PeopleViewProps> = ({ selectedCompanyId }) => {
  const theme    = useTheme();
  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);

  const [activeTab,        setActiveTab]        = useState<PeopleTab>("PEOPLE");
  const [search,           setSearch]           = useState('');
  const [newRoleName,      setNewRoleName]      = useState("");
  const [addToRoleUser,    setAddToRoleUser]    = useState<User_Info_Type | null>(null);
  const [deleteRoleId,     setDeleteRoleId]     = useState<number | null>(null);
  const [deleteRoleName,   setDeleteRoleName]   = useState("");

  const { data, isLoading }  = useGetUsersByCompany(selectedCompanyId);
  const currentUsers         = [...(data?.data.find(c => c.company_id === selectedCompanyId)?.user_info ?? [])].sort((a, b) =>
    `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
  );
  const filteredUsers = search
    ? currentUsers.filter(u =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
        u.email_id.toLowerCase().includes(search.toLowerCase())
      )
    : currentUsers;

  const { data: rolesData }  = useGetRolesByCompany(selectedCompanyId);
  const roles                = [...(rolesData?.data ?? [])].sort((a, b) => a.role_name.localeCompare(b.role_name));

  const createRoleMutation   = useCreateRole(selectedCompanyId);
  const deleteRoleMutation   = useDeleteRole(selectedCompanyId);

  const handleCreateRole = () => {
    if (!newRoleName.trim()) return;
    createRoleMutation.mutate(
      { company_id: selectedCompanyId, role_name: newRoleName.trim(), created_by: userInfo?.user_id ?? 0 },
      { onSuccess: () => setNewRoleName("") }
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <LoadingOverlay isLoading={isLoading} />
      <MagicOverlay show={createRoleMutation.isPending || deleteRoleMutation.isPending} />

      <Box sx={{ mx: 2, mt: 2, mb: 1.5 }}>

        {/* Tabs */}
        <Box sx={{ display: "flex", gap: 3, mb: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          {(["PEOPLE", "ROLES"] as PeopleTab[]).map(tab => (
            <Typography
              key={tab}
              variant="h5"
              onClick={() => setActiveTab(tab)}
              sx={{
                pb: 1, cursor: "pointer", fontWeight: 700,
                color: activeTab === tab ? theme.palette.text.primary : theme.palette.text.secondary,
                borderBottom: activeTab === tab
                  ? `2px solid ${theme.palette.primary.main}`
                  : "2px solid transparent",
                transition: "color 0.15s",
              }}
            >
              {tab}
            </Typography>
          ))}
        </Box>

        {/* Search bar — PEOPLE tab only */}
        {activeTab === "PEOPLE" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, mb: 1.5, border: `1px solid ${theme.palette.divider}`, borderRadius: "8px" }}>
            <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: "1.1rem" }} />
            <InputBase
              placeholder="Find people..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ flex: 1, fontSize: "0.875rem", color: theme.palette.text.primary }}
            />
          </Box>
        )}

        {/* Create Role input — ROLES tab only */}
        {activeTab === "ROLES" && (
          <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
            <InputBase
              value={newRoleName}
              onChange={e => setNewRoleName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreateRole()}
              placeholder="Role name..."
              sx={{
                flex: 1, px: 2, py: 1, borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary, fontSize: "0.9rem",
              }}
            />
            <Button
              variant="contained"
              disabled={!newRoleName.trim() || createRoleMutation.isPending}
              onClick={handleCreateRole}
              sx={{ fontWeight: 700, borderRadius: "8px", px: 2.5, fontSize: "0.8rem", whiteSpace: "nowrap" }}
            >
              {createRoleMutation.isPending ? "Creating..." : "Create Role"}
            </Button>
          </Box>
        )}
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, pb: 2 }}>
        {activeTab === "PEOPLE" ? (
          filteredUsers.length > 0 ? (
            filteredUsers.map(user => (
              <UserRow key={user.user_id} user={user} onAddToRole={setAddToRoleUser} />
            ))
          ) : (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: "center", mt: 6 }}>
              {search ? "No matching people found." : "No people found."}
            </Typography>
          )
        ) : (
          roles.length > 0 ? (
            roles.map(role => (
              <RoleRow
                key={role.role_id}
                role={role}
                companyUsers={currentUsers}
                assignedBy={userInfo?.user_id ?? 0}
                onDelete={(role_id, role_name) => { setDeleteRoleId(role_id); setDeleteRoleName(role_name); }}
              />
            ))
          ) : (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: "center", mt: 6 }}>
              No roles yet. Create one above.
            </Typography>
          )
        )}
      </Box>

      <AddToRoleDialog
        open={!!addToRoleUser}
        user={addToRoleUser}
        roles={roles}
        assignedBy={userInfo?.user_id ?? 0}
        onClose={() => setAddToRoleUser(null)}
      />

      {/* Delete Role Confirmation */}
      <Dialog
        open={deleteRoleId !== null}
        onClose={() => setDeleteRoleId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: "10px" } }}
      >
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary" }}>Delete Role</Typography>
            <IconButton size="small" onClick={() => setDeleteRoleId(null)} sx={{ color: "text.secondary" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ px: 3, py: 2.5 }}>
            <Typography sx={{ color: "text.primary", fontSize: "0.95rem" }}>
              Are you sure you want to delete the role <strong>"{deleteRoleName}"</strong>? This action cannot be undone.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 3, pb: 2.5 }}>
            <Button variant="outlined" onClick={() => setDeleteRoleId(null)} sx={{ borderRadius: "8px", fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => { if (deleteRoleId !== null) deleteRoleMutation.mutate(deleteRoleId); setDeleteRoleId(null); }}
              sx={{ borderRadius: "8px", fontWeight: 600 }}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PeopleView;
